import React, { Fragment, useEffect, useRef, useState } from "react";
import styles from "./NavBarElectron.module.scss";

import CalibrateLogo from "@src/assets/CalibrateLogo.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearTokens } from "@src/app/authSlice";
import { clearUserDetails, setTheme, setUserLogin } from "@src/app/globalSlice";
import { persistor, RootState } from "@src/app/store";
import { clearHeaderState } from "@src/app/headersSlice";
import { Tooltip, Typography, toast } from "top-brains-ui";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  ChevronDown,
  RotateCw,
  Globe,
  // Sun,
  // Moon,
} from "lucide-react";
import { truncateText } from "@src/utils/CommonUtils";
import Logout from "@src/assets/website-log-out.svg";
import { Menu, X, LogOut } from "lucide-react";
import Modal from "../modal/Modal";
import PortalModal from "../modal/PortalModal";
import WebPreview from "../web-preview/WebPreview";
import { isElectronApp } from "@src/utils/envUtils";
import { storageService } from "@src/services/StorageService";
import { clearLogoutSession } from "@src/utils/logoutUtils";
import { ExitingLoader } from "../exiting-loader/ExitingLoader";
import { stopDockerDesktopSilently } from "@src/utils/devopsUtils";
import HeaderDownloadMenu from "../video-downloader/HeaderDownloadMenu";

const themePalette = {
  website: {
    textPrimary: "#f0f0f5",
    textSecondary: "#8b8ba0",
    border: "#2e2e40",
    surfaceBg: "#1a1a24",
    surfaceHover: "#262636",
    accent: "#5b5bf0",
    accentText: "#ffffff",
    logoAccent: "#5b5bf0",
    themeClass: styles.websiteTheme,
  },
  app: {
    textPrimary: "#1a1a2e",
    textSecondary: "#6b7280",
    border: "#e5e5ef",
    surfaceBg: "#ffffff",
    surfaceHover: "#f8f8fc",
    accent: "#4f46e5",
    accentText: "#ffffff",
    logoAccent: "#4f46e5",
    themeClass: styles.appTheme,
  },
};

export interface SubmenuItem {
  label: string;
  onClick: (link: string) => void;
  icon?: React.ReactNode | string;
}

export interface LinkItem {
  label: string;
  onClick?: () => void;
  authRequired?: boolean;
  authRedirect?: string;
  submenu?: SubmenuItem[];
}

interface NavbarProps {
  links?: LinkItem[];
  showLinks?: boolean;
  showLogo?: boolean;
}

const NavBarElectron: React.FC<NavbarProps> = ({
  links = [],
  showLinks = true,
  showLogo = true,
}) => {
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated = !!accessToken;
  const dispatch = useDispatch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [clickedLink, setClickedLink] = useState<string | null>(null);
  const isLogin = useSelector((state: RootState) => state.globalSlice.isLogin);
  const { userDetails, theme } = useSelector(
    (state: RootState) => state.globalSlice,
  );
  const location = useLocation();
  // const isStudent =
  //   userDetails?.currentPrivilege?.toLowerCase?.() === "student";
  const isDarkTheme = theme === "website-theme";
  const activeTheme = isDarkTheme ? themePalette.website : themePalette.app;
  const [showExitModal, setShowExitModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isElectron = isElectronApp();
  const isVideoDownloading = useSelector((state: RootState) => {
    const vd = state?.videoDownload;
    if (!vd) return false;

    const hasNoQueuedTasks = !vd.downloadQueue || vd.downloadQueue.length === 0;

    // If download completed or failed, show reload
    const isCompleted = vd.downloadStage === "completed";
    const isFailed =
      vd.downloadStage === "failed" ||
      (vd.downloadingNodeId &&
        Boolean(vd.failedTasks?.[vd.downloadingNodeId])) ||
      (vd.currentTask?.nodeId &&
        Boolean(vd.failedTasks?.[vd.currentTask.nodeId]));

    if ((isCompleted || isFailed) && hasNoQueuedTasks) {
      return false;
    }

    return Boolean(vd.isDownloading || vd.currentTask);
  });

  const isLoginPage =
    location.pathname === "/login" || location.pathname === "/";
  const [hasDefaultedTheme, setHasDefaultedTheme] = useState(false);
  const [lastPath, setLastPath] = useState("");

  useEffect(() => {
    if (isElectron) {
      if (isLoginPage) {
        if (lastPath !== location.pathname || !hasDefaultedTheme) {
          setLastPath(location.pathname);
          setHasDefaultedTheme(true);
          if (theme !== "app-theme") {
            dispatch(setTheme("app-theme"));
          }
        }
      } else {
        if (hasDefaultedTheme) {
          setHasDefaultedTheme(false);
          setLastPath("");
        }
      }
    }
  }, [
    isElectron,
    isLoginPage,
    location.pathname,
    theme,
    hasDefaultedTheme,
    lastPath,
    dispatch,
  ]);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  const navRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const activeMenuRef = useRef<string | null>(null);

  useEffect(() => {
    activeMenuRef.current = activeMenu;
  }, [activeMenu]);

  useEffect(() => {
    if (!isLogin) {
      const pathToLabel: Record<string, string> = {
        "/challenges": "Challenges",
        "/website-quiz": "Quiz",
        "/website-tournaments": "Tournaments",
        "/student-organization": "My Org's",
        "/quiz": "My Org's",
        "/organizations": "My Org's",
        // "/subjects": "Learn",
      };

      const matchedLabel = Object.entries(pathToLabel).find(([path]) =>
        location.pathname.startsWith(path),
      )?.[1];

      if (isAuthenticated) {
        setClickedLink(matchedLabel || "Learn");
      } else {
        setClickedLink(null);
      }

      setSubmenuOpen(false);
      setActiveMenu(null);
    }
  }, [isLogin, location.pathname, isAuthenticated]);

  const handleLogout = () => {
    setSubmenuOpen(false);
    dispatch(clearUserDetails());
    dispatch(clearHeaderState());
    dispatch(clearTokens());
    dispatch(setTheme("website-theme"));
    persistor.purge();
    setClickedLink("");
    navigate("/login");
  };

  const handleExit = async () => {
    setIsExiting(true);
    (window as unknown as { isExiting?: boolean }).isExiting = true;
    try {
      const activeUser = userDetails?.name || "guest";
      await stopDockerDesktopSilently(activeUser);
    } catch (error) {
      console.error("Failed to stop Docker on exit:", error);
    }
    try {
      const envFolder = import.meta.env.VITE_EXTRACTED_RESOURCE_FOLDER;
      if (envFolder) {
        await storageService.deletePath([envFolder]);
      }
    } catch (error) {
      console.error("Failed to delete extracted resources on exit:", error);
    }
    if (isElectron) {
      clearLogoutSession(dispatch, { theme: "app-theme" });
      (
        window as unknown as { electronApp: { quitApp: () => void } }
      ).electronApp.quitApp();
    } else {
      setIsExiting(false);
      (window as unknown as { isExiting?: boolean }).isExiting = false;
      setShowExitModal(false);
    }
  };

  // const handleThemeToggle = () => {
  //   dispatch(setTheme(isDarkTheme ? "app-theme" : "website-theme"));
  //   setSubmenuOpen(false);
  //   setMobileMenuOpen(false);
  // };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        submenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target)
      ) {
        setSubmenuOpen(false);
      }

      if (navRef.current && !navRef.current.contains(target)) {
        setActiveMenu(null);
        if (mobileMenuOpen) {
          setMobileMenuOpen(false);
        }
        if (activeMenuRef.current === "Challenges") {
          setClickedLink(null);
        }
      }
    };

    if (submenuOpen || mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [submenuOpen, mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileMenuOpen]);

  if ((window as unknown as { isExiting?: boolean }).isExiting || isExiting) {
    return <ExitingLoader theme={theme} isExiting={isExiting} />;
  }

  return (
    <>
      <div
        className={`${styles.navbar} ${activeTheme.themeClass}`}
        ref={navRef}
      >
        {showLogo && (
          <div
            className={styles.logo}
            onClick={() => {
              setClickedLink(null);
              if (isAuthenticated) {
                navigate("/learn");
              } else {
                navigate("/");
              }
            }}
          >
            <img
              src={CalibrateLogo}
              alt="Calibrate Logo"
              className={styles.calibrateLogo}
            />
            <div className={styles.webName}>
              <Tooltip title={isOnline ? "Online" : "Offline"}>
                <div
                  className={`${styles.statusDot} ${isOnline ? styles.online : styles.offline}`}
                />
              </Tooltip>
              {isElectron && !isVideoDownloading && (
                <Tooltip title="Reload">
                  <button
                    type="button"
                    className={styles.reloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.electronApp?.hardReload) {
                        window.electronApp.hardReload();
                      } else {
                        window.location.reload();
                      }
                    }}
                  >
                    <RotateCw size={14} />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        <div
          className={styles.mobileMenuIcon}
          onClick={() => setMobileMenuOpen((prev) => !prev)}
        >
          {mobileMenuOpen ? (
            <X size={24} color={activeTheme.textPrimary} />
          ) : (
            <Menu size={24} color={activeTheme.textPrimary} />
          )}
        </div>

        <div className={styles.navbarRightSection}>
          {isElectron && (
            <div className={styles.links} style={{ marginRight: "10px" }}>
              <HeaderDownloadMenu />
              {/* <Tooltip title="Search the web">
                <span
                  className={styles.link}
                  onClick={() => setShowSearchModal(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Globe size={18} />
                </span>
              </Tooltip> */}
              <span
                className={styles.link}
                onClick={() => setShowExitModal(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <LogOut size={18} />
                Exit
              </span>
            </div>
          )}
          {showLinks && (
            <div className={styles.links}>
              {links.map((link, index) => (
                <Tooltip key={`navbar-link-${link.label}-${index}`}>
                  <span
                    className={`${styles.link} ${
                      clickedLink === link.label ? styles.clicked : ""
                    }`}
                    onClick={() => {
                      if (link.submenu) {
                        if (activeMenu === link.label) {
                          setActiveMenu(null);
                        } else {
                          setActiveMenu(link.label);
                          setSubmenuOpen(true);
                        }
                      } else if (link.onClick) {
                        link.onClick();
                      }

                      if (link.label.toLowerCase() !== "challenges") {
                        setSubmenuOpen(false);
                        setActiveMenu(null);
                      } else if (link.label.toLowerCase() == "challenges") {
                        // setSubmenuOpen(true);
                      }
                    }}
                  >
                    {link.label}
                    {link.submenu &&
                      activeMenu === link.label &&
                      submenuOpen && (
                        <div className={styles.submenu}>
                          <div className={styles.submenuContent}>
                            {link.submenu.map((submenuItem, subIndex) => (
                              <Fragment key={`submenu-item-${subIndex}`}>
                                <Tooltip
                                  title={
                                    submenuItem.label.length > 22
                                      ? submenuItem.label
                                      : ""
                                  }
                                >
                                  <div
                                    className={styles.submenuItem}
                                    onClick={() => {
                                      submenuItem?.onClick(
                                        submenuItem.label.toLowerCase(),
                                      );
                                      setSubmenuOpen(false);
                                      setActiveMenu(null);
                                    }}
                                  >
                                    <img
                                      alt="submenu icon"
                                      src={`data:image/png;base64,${submenuItem.icon}`}
                                      className={styles.submenuIcon}
                                    />
                                    <span>{submenuItem.label}</span>
                                  </div>
                                </Tooltip>
                              </Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                  </span>
                </Tooltip>
              ))}
            </div>
          )}
          <div className={styles.signIn}>
            {!isAuthenticated ? (
              /* isElectron && (
                <button
                  type="button"
                  className={styles.themeToggle}
                  onClick={handleThemeToggle}
                  title={
                    isDarkTheme
                      ? "Switch to light theme"
                      : "Switch to dark theme"
                  }
                >
                  {isDarkTheme ? (
                    <Sun size={18} color={activeTheme.textPrimary} />
                  ) : (
                    <Moon size={18} color={activeTheme.textPrimary} />
                  )}
                </button>
              ) */
              null
            ) : (
              <div className={styles.authControls}>
                {/* {isElectron && (
                  <button
                    type="button"
                    className={styles.themeToggle}
                    onClick={handleThemeToggle}
                    title={
                      isDarkTheme
                        ? "Switch to light theme"
                        : "Switch to dark theme"
                    }
                  >
                    {isDarkTheme ? (
                      <Sun size={18} color={activeTheme.textPrimary} />
                    ) : (
                      <Moon size={18} color={activeTheme.textPrimary} />
                    )}
                  </button>
                )} */}
                <div
                  ref={profileMenuRef}
                  className={styles.avatarWrapper}
                  onClick={() => setSubmenuOpen((prev) => !prev)}
                >
                  <div className={styles.avatar}>
                    {userDetails?.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <ChevronDown
                    width={14}
                    height={16}
                    strokeWidth={2}
                    color={activeTheme.textPrimary}
                  />
                  {submenuOpen && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "240px",
                        border: `1px solid ${activeTheme.border}`,
                        position: "absolute",
                        top: "44px",
                        right: "0px",
                        backgroundColor: activeTheme.surfaceBg,
                        borderRadius: "8px",
                        boxShadow:
                          theme === "app-theme"
                            ? "0 18px 40px rgba(62, 89, 74, 0.18)"
                            : "0 18px 40px rgba(0, 0, 0, 0.35)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          height: "49px",
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          borderBottom: `1px solid ${activeTheme.border}`,
                          padding: "4px 16px",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <div
                          style={{
                            height: "40px",
                            width: "40px",
                            borderRadius: "50%",
                            backgroundColor: activeTheme.accent,
                            color: activeTheme.accentText,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {userDetails?.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div
                          style={{
                            color: activeTheme.textPrimary,
                            transition: "color 0.3s ease",
                          }}
                        >
                          {truncateText(userDetails?.name, 13)}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          padding: "8px",
                          alignItems: "center",
                          height: "36px",
                          cursor: "pointer",
                          color: activeTheme.textPrimary,
                          background: "transparent",
                          transition: "color 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            activeTheme.surfaceHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                        onClick={handleLogout}
                      >
                        <img src={Logout} height={16} width={16} />
                        <Typography
                          fontSize={14}
                          color={activeTheme.textPrimary}
                        >
                          Log out
                        </Typography>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {mobileMenuOpen && (
          <div className={styles.mobileMenuContainer}>
            <div className={styles.mobileLinks}>
              {links.map((link, index) => (
                <div
                  key={`mobile-link-${index}`}
                  className={styles.mobileLink}
                  onClick={() => {
                    if (link.onClick) link.onClick();
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </div>
              ))}

              {isAuthenticated ? (
                <>
                  <div
                    className={styles.mobileLink}
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Logout
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={styles.mobileLink}
                    onClick={() => {
                      navigate("/login");
                      // setMobileMenuOpen(false);
                      dispatch(setUserLogin(true));
                    }}
                  >
                    Sign In
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {showExitModal && (
        <Modal
          isOpen={showExitModal}
          headerTitle="Exit Application"
          body={
            <Typography fontSize={14} fontWeight="regular">
              {isExiting
                ? "Exiting the Application. Please wait..."
                : "Are you sure you want to exit the application?"}
            </Typography>
          }
          primaryButtonText={isExiting ? "Exiting..." : "Exit"}
          secondaryButtonText="Cancel"
          disablePrimaryButton={isExiting}
          showSecondaryButton={!isExiting}
          showCloseButton={!isExiting}
          closeOnPrimaryClick={false}
          onClose={() => {
            if (!isExiting) setShowExitModal(false);
          }}
          onSecondaryButtonClick={() => {
            if (!isExiting) setShowExitModal(false);
          }}
          onPrimaryButtonClick={handleExit}
          width="400px"
          height="180px"
        />
      )}
      {showSearchModal && (
        <PortalModal
          isOpen={showSearchModal}
          headerTitle="Search"
          showFooter={false}
          showCloseButton
          closeOnOutsideClick={false}
          width="92vw"
          height="90vh"
          onClose={() => setShowSearchModal(false)}
          body={
            <WebPreview
              url="https://www.google.com"
              height="100%"
              width="100%"
            />
          }
        />
      )}
    </>
  );
};

export default NavBarElectron;
